
import { wsurl } from "@/config"
import { encryptAES ,decryptAES,isBase64String } from '@/utils/crypto'
// WebSocket服务类
class WebSocketService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 10
    this.reconnectTimeout = null
    this.connecting = false
    this.heartbeatInterval = null
    this.heartbeatIntervalMs = 5000
    this.lastHeartbeat = null
    this.lastPongReceived = null
    this.intentionallyDisconnected = false // 标记是否主动断开
    this.authInvalidated = false

    // 消息类型常量 (根据后端协议)
    this.MESSAGE_TYPES = {
      TEXT: 'TEXT',
      IMAGE: 'IMAGE',
      FILE: 'FILE',
      VOICE: 'VOICE',
      VIDEO: 'VIDEO'
    }

    // 事件类型常量
    this.EVENTS = {
      PING: 'PING',
      PONG: 'PONG',
      MESSAGE: 'MESSAGE',
      READ: 'READ',
      ACK: 'ACK',
      RECALL: 'RECALL',
      ERROR: 'ERROR',
      SESSION_UPDATE: 'SESSION_UPDATE',
      FORCE_LOGOUT: 'FORCE_LOGOUT'
    }

    // 事件处理器
    this.messageHandlers = []
    this.systemHandlers = []
    this.statusHandlers = []
    this.pendingMessages = new Map()
  }

  invalidateAuth() {
    this.authInvalidated = true
    this.disconnect()
  }

  clearAuthInvalidation() {
    this.authInvalidated = false
  }

  // 连接到WebSocket服务器
  connect(token, autoReconnect = true) {
    
    if (this.connecting) {
      console.log('WebSocket connection already in progress')
      return Promise.reject(new Error('Connection already in progress'))
    }

    const authTokenSnapshot = localStorage.getItem('auth_token')
    if (this.authInvalidated && !authTokenSnapshot) {
      return Promise.reject(new Error('Auth invalidated'))
    }
    if (this.authInvalidated && authTokenSnapshot) {
      this.authInvalidated = false
    }

    this.intentionallyDisconnected = false

    this.connecting = true

    return new Promise((resolve, reject) => {
      try {
        const authToken = localStorage.getItem('auth_token')
        if (!authToken) {
          this.connecting = false
          return reject(new Error('No auth token found in local storage'))
        }
        const baseUrl = wsurl
        const url = `${baseUrl}?${authToken ? 'token=' + authToken : ''}`

        this.socket = new WebSocket(url)
        this.socket.binaryType = 'blob'

        this.socket.onopen = () => {
          console.log('WebSocket connected successfully')
          this.isConnected = true
          this.reconnectAttempts = 0
          this.connecting = false
          this.intentionallyDisconnected = false // 重置标志：连接成功时
          this.lastPongReceived = Date.now()
          this._startHeartbeat()
          this._notifySystem('connect')
          resolve()
        }

        this.socket.onclose = (event) => {
          console.log('WebSocket disconnected', event)
          this.isConnected = false
          this.connecting = false
          this._notifySystem('disconnect')

          // 只有在非主动断开且启用自动重连时才重连
          if (!this.intentionallyDisconnected && autoReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectTimeout = setTimeout(() => {
              this.reconnectAttempts++
              console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
              this.connect(token, autoReconnect).catch(err => {
                console.error('Reconnection failed:', err)
              })
            }, 5000)
          }
        }

        this.socket.onerror = (error) => {
          console.error('WebSocket connection error:', error)
          this.connecting = false
          this.isConnected = false

          const errorDetails = {
            message: error.message || 'Unknown WebSocket error',
            type: error.type || 'websocket_error',
            timestamp: new Date().toISOString(),
            url: url,
            readyState: this.socket ? this.socket.readyState : 'unknown'
          }

          console.error('Detailed WebSocket error:', errorDetails)
          this._notifySystem('error', errorDetails)
          reject(error)
        }

        this.socket.onmessage = (event) => {
          try {
            if (event.data instanceof Blob || event.data instanceof ArrayBuffer) {
              const reader = new FileReader()
              reader.onload = () => {
                try {
                  const textData = reader.result
                  this._processMessageData(textData)
                } catch (convertError) {
                  console.error('Error converting binary message:', convertError)
                }
              }
              reader.readAsText(event.data instanceof Blob ? event.data : new Blob([event.data]))
            } else {
              this._processMessageData(event.data)
            }
          } catch (error) {
            console.error('Error processing WebSocket message:', error)
          }
        }
      } catch (error) {
        this.connecting = false
        reject(error)
      }
    })
  }

  // 断开连接
  disconnect() {
    console.log('主动断开 WebSocket 连接')
    
    // 标记为主动断开，防止自动重连
    this.intentionallyDisconnected = true
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.socket) {
      // 清空所有事件处理器，防止任何遗留的消息处理
      this.socket.onopen = null
      this.socket.onclose = null
      this.socket.onerror = null
      this.socket.onmessage = null
      
      this.socket.close()
      this.socket = null
    }

    this.isConnected = false
    this.connecting = false
    this.reconnectAttempts = 0
  }

  // 发送消息
  send(data) {
    if (!this.isConnected || !this.socket) {
      throw new Error('WebSocket is not connected')
    }
    data.data = encryptAES(data.data)
    const message = JSON.stringify(data)
    this.socket.send(message)
  }

  // 发送文本消息
  sendMessage(content, receiverId, senderId, messageId) {
    if (!this.isConnected) {
      console.error('WebSocket is not connected')
      return false
    }

    try {
      const messageData = {
        event: this.EVENTS.MESSAGE,
        data:{
          content: content,
          sendTime: Date.now(),
          messageType: this.MESSAGE_TYPES.TEXT,
          messageId: messageId,
          // 如果是客服发送，必须指定receiverId；普通用户不传，让后端自动分配
          ...(receiverId && { receiverId: receiverId })
        }
      }
      this.send(messageData)
    

      // 将消息添加到待确认队列
      this.pendingMessages.set(messageId, {
        type: 'message',
        timestamp: Date.now()
      })

      return true
    } catch (error) {
      console.error('发送消息失败:', error)
      return false
    }
  }

  // 发送图片消息
  sendImageMessage(imageUrl, receiverId, senderId, messageId) {
    if (!this.isConnected) {
      console.error('WebSocket is not connected')
      return false
    }

    try {
      const messageData = {
        event: this.EVENTS.MESSAGE,
        data: {
          content: imageUrl,
          sendTime: Date.now(),
          messageType: this.MESSAGE_TYPES.IMAGE,
          messageId: messageId || Date.now().toString(),
          // 如果是客服发送，必须指定receiverId；普通用户不传，让后端自动分配
          ...(receiverId && { receiverId: receiverId })
        }
      }

      this.send(messageData)
      console.log('发送图片消息:', messageData)

      // 将消息添加到待确认队列
      this.pendingMessages.set(messageId || Date.now().toString(), {
        type: 'image',
        timestamp: Date.now()
      })

      return true
    } catch (error) {
      console.error('发送图片消息失败:', error)
      return false
    }
  }

  // 发送视频消息
  sendVideoMessage(videoUrl, receiverId, senderId, messageId) {
    if (!this.isConnected) {
      console.error('WebSocket is not connected')
      return false
    }

    try {
      const messageData = {
        event: this.EVENTS.MESSAGE,
        data: {
          content: videoUrl,
          sendTime: Date.now(),
          messageType: this.MESSAGE_TYPES.VIDEO, // 'VIDEO'
          messageId: messageId || Date.now().toString(),
          ...(receiverId && { receiverId: receiverId })
        }
      }

      this.send(messageData)
      console.log('发送视频消息:', messageData)

      this.pendingMessages.set(messageId || Date.now().toString(), {
        type: 'video',
        timestamp: Date.now()
      })

      return true
    } catch (error) {
      console.error('发送视频消息失败:', error)
      return false
    }
  }

  // 发送文件消息
  sendFileMessage(fileInfo, receiverId, messageId) {
    if (!this.isConnected) {
      console.error('WebSocket is not connected')
      return false
    }

    try {
      const messageData = {
        event: this.EVENTS.MESSAGE,
        data:{
          content: fileInfo.url,
          sendTime: Date.now(),
          messageType: this.MESSAGE_TYPES.FILE,
          messageId: messageId || Date.now().toString(),
          // 如果是客服发送，必须指定receiverId；普通用户不传，让后端自动分配
          ...(receiverId && { receiverId: receiverId }),
          // 文件额外信息
          fileName: fileInfo.name,
          fileSize: fileInfo.size,
          fileType: fileInfo.type
        }
      }

      this.send(messageData)
      console.log('发送文件消息:', messageData)

      // 将消息添加到待确认队列
      this.pendingMessages.set(messageId || Date.now().toString(), {
        type: 'file',
        timestamp: Date.now()
      })

      return true
    } catch (error) {
      console.error('发送文件消息失败:', error)
      return false
    }
  }

  // 添加消息处理器
  addMessageHandler(handler) {
    this.messageHandlers.push(handler)
  }

  // 移除消息处理器
  removeMessageHandler(handler) {
    const index = this.messageHandlers.indexOf(handler)
    if (index > -1) {
      this.messageHandlers.splice(index, 1)
    }
  }

  // 添加系统处理器
  addSystemHandler(handler) {
    this.systemHandlers.push(handler)
  }

  // 移除系统处理器
  removeSystemHandler(handler) {
    const index = this.systemHandlers.indexOf(handler)
    if (index > -1) {
      this.systemHandlers.splice(index, 1)
    }
  }

  // 添加状态处理器
  addStatusHandler(handler) {
    this.statusHandlers.push(handler)
  }

  // 移除状态处理器
  removeStatusHandler(handler) {
    const index = this.statusHandlers.indexOf(handler)
    if (index > -1) {
      this.statusHandlers.splice(index, 1)
    }
  }

  // 私有方法：启动心跳
  _startHeartbeat() {
    if(this.heartbeatInterval)
      clearInterval(this.heartbeatInterval)
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected && this.socket) {
        const pingData = {
          event: this.EVENTS.PING,
          data: {
            timestamp: Date.now()
          }
        }
        this.socket.send(JSON.stringify(pingData))
        this.lastHeartbeat = Date.now()
      }
    }, this.heartbeatIntervalMs)
  }

  // 私有方法：处理消息数据
  _processMessageData(data) {   
  
     
    try {
      const message = JSON.parse(data)
    
      // 处理PONG心跳响应
      if (message.event === this.EVENTS.PONG) {
        this.lastPongReceived = Date.now()
        return
      }

      // 处理MESSAGE事件（收到新消息）
      if (message.event === this.EVENTS.MESSAGE) {
        if(isBase64String(message.data)){
          message.data=decryptAES(message.data)
        }
        
        
        this._notifyMessageHandlers(message)
      }
      else if (message.event === this.EVENTS.READ) {
        this._notifyMessageHandlers(message)
      }
    
      // 处理ACK确认事件
      else if (message.event === this.EVENTS.ACK) {
        this._handleAck(message.data)
      }
      // 处理RECALL撤回事件
      else if (message.event === this.EVENTS.RECALL) {
        this._handleRecall(message.data)
      }
      // 处理ERROR错误事件
      else if (message.event === this.EVENTS.ERROR) {
        this._notifySystem('error', message.data)
      }
      // 处理SESSION_UPDATE会话更新
      else if (message.event === this.EVENTS.SESSION_UPDATE) {
        this._notifySystem('session_update', message.data)
      }
      // 处理FORCE_LOGOUT强制下线
      else if (message.event === this.EVENTS.FORCE_LOGOUT) {
        this._notifySystem('force_logout', message.data)
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error, data)
    }
  }

  // 私有方法：通知消息处理器
  _notifyMessageHandlers(message) {
    console.log(message,'=====================================');
    this.messageHandlers.forEach(handler => {
      try {
        handler(message)
      } catch (error) {
        console.error('Error in message handler:', error)
      }
    })
  }

  // 私有方法：处理ACK确认
  _handleAck(data) {
    console.log('收到消息确认:', data)
    // 可以在这里更新消息状态为已送达
    if (this.pendingMessages.has(data.messageId)) {
      const pendingMsg = this.pendingMessages.get(data.messageId)
      console.log('Pending message found for ACK:', pendingMsg)
      // 通知消息状态更新
      if (pendingMsg.onAck) {
        pendingMsg.onAck(data)
      }
      this.pendingMessages.delete(data.messageId)
    }
  }

  // 私有方法：处理撤回消息
  _handleRecall(data) {
    console.log('收到撤回消息:', data)
    // 通知撤回消息处理
    this._notifySystem('recall', data)
  }

  // 私有方法：通知系统处理器
  _notifySystem(event, data) {
    this.systemHandlers.forEach(handler => {
      try {
        handler(event, data)
      } catch (error) {
        console.error('Error in system handler:', error)
      }
    })
  }
}

// 创建单例实例
const wsService = new WebSocketService()

export default wsService
